/**
 * Stripe.js error types whose `message` is written for the customer (Stripe documents
 * these as safe to display): a bank decline ("Your card's security code is incorrect.")
 * or a field-level validation error ("Your card number is incomplete.").
 */
const CUSTOMER_FACING_STRIPE_ERROR_TYPES = ['card_error', 'validation_error'];

/**
 * Pick the message to show a customer for a Stripe.js failure (a `confirmSetup` /
 * `confirmPayment` `result.error`, or anything it threw).
 *
 * Only `card_error` / `validation_error` messages are shown as-is. Everything else —
 * a thrown `IntegrationError` ("You specified 'never' for fields.billing_details.email…"),
 * `invalid_request_error`, `api_error`, `api_connection_error`, `rate_limit_error`,
 * `authentication_error`, or an error with no type — is developer-facing text, so the
 * caller's generic `fallback` is returned instead. Report the raw error to telemetry
 * separately; this only decides what the customer reads.
 */
export function getCustomerFacingStripeMessage(error: unknown, fallback: string): string {
  if (typeof error !== 'object' || error === null) return fallback;
  const type = 'type' in error ? error.type : undefined;
  const message = 'message' in error ? error.message : undefined;
  if (
    typeof type === 'string' &&
    CUSTOMER_FACING_STRIPE_ERROR_TYPES.includes(type) &&
    typeof message === 'string' &&
    message.trim()
  ) {
    return message;
  }
  return fallback;
}

/**
 * Classify an `addNewSubAccount` / `addNewUserTeamBilling` backend error
 * into card-related (Stripe rejected the payment method) vs
 * application-level (duplicate email, missing zip, plan disallowed, etc.).
 *
 * Returns `true` when the failure is a Stripe payment-method rejection —
 * in which case the dialog SHOULD remount the Stripe `PaymentElement` so
 * the user can re-enter card details. Returns `false` for application
 * errors — in which case the card MUST be preserved (issue #1339).
 *
 * Conservative default: unknown errors return `false` (keep card). The
 * next Stripe `confirmSetup` will surface a clear inline error inside
 * the PaymentElement if the card itself is bad.
 */
export function isCardRelatedBillingError(
  code: string | undefined,
  message: string | undefined,
): boolean {
  if (code === 'STRIPE_FAILED') return true;
  if (!message) return false;
  return /card\s+declined|expired\s+card|insufficient\s+funds|payment\s+method|incorrect\s+cvc|invalid\s+card|card\s+number\s+is\s+invalid/i.test(
    message,
  );
}
